import {
  formatDate,
  formatISO,
  getKoreanTime,
  ignoreTimezone,
  parseISO,
} from "@enkor/date-fns";

import {
  EventInfoCategoryInfo,
  ResponseEventDetailV1,
} from "@models/data-contracts";
import countries from "@public/data/country.json";
import { GenderValueType } from "@domains/common/types/common";
import { GENDER_OPTION } from "@domains/common/constants/wordsMapper";

import ParticipantViewModel from "./ParticipantViewModel";

class EventViewModel {
  id: number;
  attendanceFee: string;
  location: {
    areaName: string;
    nearStation: string;
    spotName: string;
    geo: {
      lat: number;
      lng: number;
    };
    placeId?: string;
    description?: string;
  };
  imageUrlList: string[];
  categories: number[];

  title: string;
  description: string;
  date: string;
  time: string;
  isClosed: boolean;
  isFull: boolean;
  host: {
    id: number;
    name: string;
    genderForDisplay: GenderValueType;
    nationality: string;
    profileImage?: string;
  };

  participant: {
    count: number;
    MaxCount: number;
    displayCountText: string;
    list: ParticipantViewModel[];
  };

  participantCount: number;
  maxParticipantCount: number;
  participantList?: ParticipantViewModel[];
  categoryList: EventInfoCategoryInfo[];

  chatId: string;

  isPartOfProject: boolean;
  project: {
    id: number;
    title: string;
    isMember: boolean;
  };

  static create(responseData: ResponseEventDetailV1) {
    const event = new EventViewModel();

    event.id = responseData.id;

    // HACK: UTC(000Z) 기준 timezone만 변경하여 한국시간으로 변경해서 사용. (모든 나라에서 한국 시간 기준으로 디스플레이)
    event.date = formatDate({
      date: formatISO(
        getKoreanTime(parseISO(ignoreTimezone(responseData.startDate))),
      ),
      type: "ENG_MONTH_DAY_YEAR",
    });

    event.time = formatDate({
      date: formatISO(
        getKoreanTime(parseISO(ignoreTimezone(responseData.startDate))),
      ),
      customType: "hh:mm aa",
    });

    event.title = responseData.title;
    event.description = responseData.description;
    event.imageUrlList = responseData.imageList.map(({ url }) => url);
    event.categories = responseData.categoryList.map(({ id }) => id);

    event.location = {
      areaName: responseData.location.englishAddress,
      nearStation: responseData.location.nearLocation
        ? `${responseData.location.nearLocation} ${
            responseData.location.nearLocation.includes("Station")
              ? ""
              : "Station"
          }`
        : responseData?.location.englishAddress,
      spotName: responseData.location.detailAddress || "",
      geo: {
        lat: responseData.location.lat,
        lng: responseData.location.lng,
      },
      placeId: responseData.location?.placeId || "",
      description: responseData.location?.description || "",
    };

    event.attendanceFee =
      responseData.fee === 0
        ? "FREE"
        : `${responseData?.fee.toLocaleString()} KRW`;

    event.isClosed = responseData.isClosed === "Y";
    event.isFull = responseData.memberList.length >= responseData.maxPeople;

    event.host = {
      id: responseData.host.id,
      name: responseData.host.name,
      genderForDisplay: responseData.host.gender
        ? GENDER_OPTION[responseData.host.gender]
        : "-",
      nationality: responseData.host.national
        ? countries[responseData.host.national]
        : "",
      profileImage: responseData.host.url,
    };

    event.participantCount = responseData?.memberList.length;
    event.maxParticipantCount = responseData.maxPeople;
    event.participantList = responseData.memberList.map((member) =>
      ParticipantViewModel.create(member),
    );

    event.participant = {
      count: responseData?.memberList.length,
      MaxCount: responseData.maxPeople,
      displayCountText: `${responseData?.memberList.length}/${responseData.maxPeople}`,
      list: responseData.memberList.map((member) =>
        ParticipantViewModel.create(member),
      ),
    };

    event.categoryList = responseData.categoryList;

    event.chatId = String(responseData.eventChatRoomId);

    event.isPartOfProject = !!responseData.project.id;
    event.project = {
      id: responseData.project.id,
      title: responseData.project.title,
      isMember: responseData.project.payYn === "Y",
    };

    return event;
  }
}

export default EventViewModel;
